import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Calendar, Video, Loader2, XCircle, ArrowRight, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDateTime = (dateOrIso, time) => {
  if (!dateOrIso) return '';
  try {
    // Brief Jul 6 2026 P0 §2 — parse defensively:
    //   • ISO datetime string ("2026-07-10T11:00:00Z" or naive) → use directly
    //   • YYYY-MM-DD + optional time HH:mm → combine
    //   • Date object → use directly
    let d;
    if (dateOrIso instanceof Date) {
      d = dateOrIso;
    } else if (typeof dateOrIso === 'string' && dateOrIso.includes('T')) {
      d = new Date(dateOrIso);
    } else {
      d = new Date(`${dateOrIso}T${time || '00:00'}`);
    }
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const presetBookingId = params.get('booking_id');

  const [status, setStatus] = useState(presetBookingId ? 'paid' : 'polling');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const loadBooking = useCallback(async (bookingId) => {
    try {
      const { data } = await axios.get(`${API}/bookings/${bookingId}`, { withCredentials: true });
      setBooking(data);
    } catch (e) {
      // ignore; fallback UI will handle
    }
  }, []);

  // Polling Stripe checkout status
  useEffect(() => {
    if (!sessionId || presetBookingId) return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      setPollCount(attempts);
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (data.status === 'paid' || data.transaction_status === 'completed') {
          setStatus('paid');
          if (data.booking_id) loadBooking(data.booking_id);
          return; // stop polling
        }
        if (data.status === 'expired' || attempts >= 15) {
          setStatus('timeout');
          setError(data.status === 'expired' ? 'Session de paiement expirée' : 'Délai dépassé');
          return;
        }
        // keep polling
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= 3) {
          setStatus('error');
          setError(e.response?.data?.detail || e.message);
        } else {
          setTimeout(poll, 2000);
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, presetBookingId, loadBooking]);

  // Load booking for preset (free) bookings
  useEffect(() => {
    if (presetBookingId) loadBooking(presetBookingId);
  }, [presetBookingId, loadBooking]);

  // -------- Renders --------
  if (status === 'polling') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="payment-polling">
        <Loader2 className="h-10 w-10 animate-spin text-purple-600 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Validation de votre paiement…</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Merci de patienter, on confirme la transaction auprès de Stripe (essai {pollCount}/15).
        </p>
      </div>
    );
  }

  if (status === 'timeout' || status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="payment-error">
        <XCircle className="h-12 w-12 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Paiement non confirmé</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md">
          {error || 'La confirmation de votre paiement a échoué. Vérifiez votre dashboard ou réessayez.'}
        </p>
        <button
          data-testid="payment-retry-dashboard"
          onClick={() => navigate('/app/dashboard?tab=bookings')}
          className="mt-6 px-5 py-2.5 bg-purple-600 text-white rounded-xl font-semibold"
        >
          Voir mes réservations
        </button>
      </div>
    );
  }

  // Success state
  const icsUrl = booking ? `${API}/bookings/${booking.booking_id}/ical` : null;

  return (
    <div className="max-w-2xl mx-auto py-10" data-testid="payment-success-page">
      <div className="text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Session confirmée !</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Votre réservation est enregistrée. Un email de confirmation vient de vous être envoyé.
        </p>
      </div>

      {booking ? (
        <div className="mt-8 p-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 space-y-3">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Expert</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {booking.expert?.display_name || 'Expert'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Date et heure</p>
            <p className="text-gray-800 dark:text-gray-100">
              {fmtDateTime(booking.date, booking.start_time)}
            </p>
          </div>
          {booking.session_type?.title && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Type de session</p>
              <p className="text-gray-800 dark:text-gray-100">
                {booking.session_type.title} · {booking.duration || booking.session_type.duration} min
              </p>
            </div>
          )}
          {booking.visio_link && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Lien visio</p>
              <a
                data-testid="payment-success-jitsi"
                href={booking.visio_link}
                target="_blank"
                rel="noreferrer"
                className="text-purple-600 hover:underline break-all"
              >{booking.visio_link}</a>
              <p className="text-xs text-gray-400 mt-1">Actif 15 minutes avant l'horaire prévu.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 p-5 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-sm text-purple-800 dark:text-purple-200">
          Chargement des détails de la réservation…
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        {booking?.visio_link && (
          <a
            data-testid="payment-success-join-btn"
            href={booking.visio_link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold"
          >
            <Video className="h-4 w-4" /> Rejoindre la session
          </a>
        )}
        {icsUrl && (
          <a
            data-testid="payment-success-ics-btn"
            href={icsUrl}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-xl font-semibold"
          >
            <Download className="h-4 w-4" /> Ajouter à mon calendrier
          </a>
        )}
        <button
          data-testid="payment-success-dashboard-btn"
          onClick={() => navigate('/app/dashboard?tab=bookings')}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-purple-600 font-semibold"
        >
          Voir toutes mes sessions <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
